<script setup lang="ts">
import { computed } from "vue";
import { format as formatDate } from "date-fns";
import { Check, ChevronsUpDown, Settings2 } from "@lucide/vue";

import Popover from "@/components/Reusables/Popover/Popover.vue";
import NavSlashDivider from "@/components/navigation/NavSlashDivider.vue";
import { useProjectRevisionsStore } from "../../stores/projectRevisions.store";
import type { ProjectRevision } from "../../types/projectRevision.types";

// The revision dropdown in the top nav, third in the organization / project / revision trail.
// Shows the revision you are looking at — the admin-set pointer by default, which arrives live via
// the ProjectActiveRevision subscription ProjectDetailLayout owns — and lets any user switch their
// own read-only preview. Changing what is current for everyone happens on the Revisions tab (admins).
// Renders nothing until the project has more than one revision — with a single save point there is
// nothing to switch between — which also keeps it out of the nav on non-project routes and in
// environments without the versioning schema.

const props = defineProps<{
  projectId: string;
}>();

const revisionsStore = useProjectRevisionsStore();

const revisions = computed<ProjectRevision[]>(() => revisionsStore.revisionsFor(props.projectId));
const activeMeta = computed(() => revisionsStore.activeMetaFor(props.projectId));
const viewingRevision = computed(() => revisionsStore.viewingRevisionFor(props.projectId));
const isPreviewing = computed(() => revisionsStore.isPreviewing(props.projectId));

// Just the name — the trail reads "organization / project / revision", and the dropdown is where
// "current" is spelled out. Previewing is called out by the amber label here and, unmissably, by
// RevisionPreviewBanner on the page itself.
const triggerLabel = computed(() => {
  if (isPreviewing.value && viewingRevision.value) return viewingRevision.value.name;
  if (activeMeta.value) return activeMeta.value.name;
  return "Revisions";
});

const savedAt = (revision: ProjectRevision): string => formatDate(new Date(revision.createdAt), "MMM d, yyyy");

const select = (revision: ProjectRevision, close: () => void) => {
  revisionsStore.setViewingRevision(props.projectId, revision.id);
  close();
};
</script>

<template>
  <Popover
    v-if="revisions.length > 1"
    placement="bottom-start"
    aria-label="Project revisions">
    <template #trigger="{ toggle, isOpen }">
      <!-- Mirrors the organization and project switchers beside it: slanted divider, name, chevron. -->
      <div class="gap-px-4 flex min-w-0 items-center px-2 py-1 pl-5.5">
        <NavSlashDivider />
        <button
          type="button"
          :aria-expanded="isOpen"
          aria-haspopup="dialog"
          class="focus-ring min-w-0 rounded-md px-1 py-0.5 leading-none"
          :title="isPreviewing ? 'Previewing a revision — read-only' : 'Current revision'"
          data-cy="revision-switcher"
          @click="toggle">
          <span
            class="block max-w-56 truncate text-sm font-medium"
            :class="isPreviewing ? 'text-app-yellow-700' : 'text-app-blue-900'">
            {{ triggerLabel }}
          </span>
        </button>
        <button
          type="button"
          :aria-expanded="isOpen"
          aria-haspopup="dialog"
          aria-label="Switch revision"
          class="focus-ring hover:bg-app-blue-200/30 duration-fast ease-standard inline-flex items-center justify-center rounded-md p-1 leading-none transition"
          @click="toggle">
          <ChevronsUpDown
            :stroke-width="1.8"
            class="icon-sm shrink-0"
            :class="isPreviewing ? 'text-app-yellow-700/70' : 'text-app-blue-800/60'"
            aria-hidden="true" />
        </button>
      </div>
    </template>

    <template #default="{ close }">
      <div class="p-px-8 flex w-72 flex-col">
        <p class="t-footnote-bold text-type-muted px-2 pb-1 tracking-wide uppercase">Revisions</p>
        <button
          v-for="revision in revisions"
          :key="revision.id"
          type="button"
          class="focus-ring interactive-active duration-fast ease-standard gap-px-8 hover:bg-app-blue-200/30 flex w-full items-center rounded-md px-2 py-1.5 text-left transition-colors"
          data-cy="revision-switcher-item"
          @click="select(revision, close)">
          <Check
            class="icon-xs shrink-0"
            :class="revision.id === viewingRevision?.id ? 'text-app-blue-700' : 'text-transparent'"
            :stroke-width="2"
            aria-hidden="true" />
          <span class="gap-px-2 flex min-w-0 flex-1 flex-col">
            <span class="t-small text-type-default truncate">{{ revision.name }}</span>
            <!-- Date only, no "v<n>": same reason the Revisions tab dropped it — a save point is
                 known by its name and when it was taken, not by a cursor into the trail. -->
            <span class="t-footnote text-type-muted">{{ savedAt(revision) }}</span>
          </span>
          <span
            v-if="revision.id === activeMeta?.id"
            class="t-footnote-bold text-app-blue-700">
            current
          </span>
        </button>
        <RouterLink
          :to="{ name: 'projectVersionHistory', params: { id: projectId }, query: { tab: 'revisions' } }"
          class="focus-ring interactive-active duration-fast ease-standard gap-px-8 text-app-blue-800 hover:bg-app-blue-200/30 mt-1 flex w-full items-center rounded-md border-t border-slate-200 px-2 py-1.5 text-left transition-colors"
          data-cy="revision-switcher-manage"
          @click="close()">
          <Settings2
            class="icon-xs shrink-0"
            :stroke-width="1.5" />
          <span class="t-caption-bold">Manage revisions</span>
        </RouterLink>
      </div>
    </template>
  </Popover>
</template>
